// AboutUs.jsx
import React, { Component } from "react";
import Datas from "../data/about-us/about-us.json";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import ModalVideo from "react-modal-video";
import "react-modal-video/css/modal-video.min.css";
import CountUp from "react-countup";
import { Styles } from "./styles/aboutUs.js";
import axios from "axios";
import { Constant, imageDomain } from "../helper/Constants.js";

class AboutUs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      about_us: {},
      youtube_id: "",
      counters: [],
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.getAboutUs();
    this.getCounters();
  }

  // Builds a usable image URL from different possible shapes
  buildImageSrc = (imgPath, fallbackFromDatas = "") => {
    const raw = (imgPath || "").toString().trim();

    // no value -> fallback to Datas file if provided
    if (!raw) {
      if (fallbackFromDatas) {
        return process.env.PUBLIC_URL + `/assets/images/${fallbackFromDatas}`;
      }
      return "";
    }

    // absolute or protocol-relative URL
    if (/^(https?:)?\/\//i.test(raw)) {
      return raw.startsWith("//") ? "https:" + raw : raw;
    }

    // server uploaded path starting with /
    if (raw.startsWith("/")) {
      const domain = imageDomain || (Constant && Constant.imageDomain) || "";
      if (domain) {
        // ensure no double slashes
        return domain.replace(/\/+$/, "") + raw;
      }
      return process.env.PUBLIC_URL + raw;
    }

    // relative filename: treat as local assets fallback
    return process.env.PUBLIC_URL + `/assets/images/${raw}`;
  };

  // Fetch About Us data (same tolerant shape handling as before)
  getAboutUs = async () => {
    this.setState({ loading: true, error: null });
    try {
      const url = Constant?.getUrls?.getAboutUs;
      if (!url) {
        // fallback to local Datas
        this.setState({
          about_us: {
            description: Datas?.description || Datas?.desc1 || "",
            youtube_id: Datas?.youtube_id || "",
            video_background: Datas?.videoBackground || "",
          },
          youtube_id: Datas?.youtube_id || "",
          loading: false,
        });
        return;
      }

      const resp = await axios.get(url);
      const doc =
        resp?.data?.data?.docs?.[0] ||
        resp?.data?.data?.[0] ||
        resp?.data?.data ||
        resp?.data?.about ||
        null;

      this.setState({
        about_us: doc || {},
        youtube_id: (doc?.youtube_id || doc?.video_id || "") + "",
        loading: false,
      });
    } catch (err) {
      console.error("getAboutUs error:", err);
      this.setState({
        about_us: {
          description: Datas?.desc1 || Datas?.description || "",
          youtube_id: Datas?.youtube_id || "",
          video_background: Datas?.videoBackground || "",
        },
        youtube_id: Datas?.youtube_id || "",
        loading: false,
        error: err,
      });
    }
  };

  // Fetch counters from Counts list API (uses your router GET /)
  getCounters = async () => {
    try {
      const url = Constant?.getUrls?.getCountsList;
      if (!url) {
        this.setState({ counters: Datas?.counters || [] });
        return;
      }

      // request with a reasonable limit to fetch all counters
      const resp = await axios.get(`${url}?limit=50&page=1`);
      // router returns { status, message, data, pagination }
      let counterList = resp?.data?.data || resp?.data?.docs || resp?.data || [];

      if (!Array.isArray(counterList)) {
        if (Array.isArray(resp?.data)) counterList = resp.data;
        else counterList = [];
      }

      // filter out soft-deleted
      counterList = counterList.filter(
        (c) => (c.is_delete || "").toString().toLowerCase() !== "yes"
      );

      if (!counterList.length && Datas?.counters?.length) {
        this.setState({ counters: Datas.counters });
        return;
      }

      this.setState({ counters: counterList });
    } catch (err) {
      console.error("getCounters error:", err);
      this.setState({ counters: Datas?.counters || [] });
    }
  };

  // Derive a numeric value from counter item
  getNumericFromItem = (item) => {
    if (!item) return 0;
    const numericFields = ["count", "value", "number", "counts"];
    for (const k of numericFields) {
      if (item[k] !== undefined && item[k] !== null && item[k] !== "") {
        const n = parseInt(item[k], 10);
        if (!isNaN(n)) return n;
      }
    }

    const desc = (item.description || "").toString();
    const match = desc.match(/(\d[\d,]*)/);
    if (match) {
      const cleaned = match[0].replace(/,/g, "");
      const n = parseInt(cleaned, 10);
      if (!isNaN(n)) return n;
    }

    return 0;
  };

  openModal = () => this.setState({ isOpen: true });
  closeModal = () => this.setState({ isOpen: false });

  render() {
    const { about_us, youtube_id, isOpen, counters } = this.state;

    // build video background (prefer about_us.video_background -> Datas.videoBackground -> YouTube thumbnail)
    let videoBgSrc =
      this.buildImageSrc(
        about_us?.video_background || about_us?.videoBackground || "",
        Datas?.videoBackground || ""
      ) || "";

    // if still empty and we have a youtube id, fallback to YouTube thumbnail
    if (!videoBgSrc && youtube_id) {
      // Remove any full URL and extract id if user accidentally provided full URL
      const maybeId = (youtube_id || "").toString().trim();
      // if full youtube url, try to extract id
      const extract = maybeId.match(
        /(?:v=|\/embed\/|\.be\/|\/v\/)([A-Za-z0-9_-]{6,})/
      );
      const id = extract ? extract[1] : maybeId;
      videoBgSrc = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }

    const descriptionHtml = about_us?.description || Datas?.desc1 || Datas?.description || "";

    // ensure displayCounters has at least 3 items (fallbacks)
    const displayCounters =
      (Array.isArray(counters) && counters.length > 0)
        ? counters
        : Datas?.counters?.length
        ? Datas.counters
        : [
            { title: "Happy Students", description: "970", count: 970 },
            { title: "Teachers", description: "130", count: 130 },
            { title: "Courses", description: "340", count: 340 },
          ];

    return (
      <Styles>
        <section className="about-us">
          <Container>
            <Row>
              {/* LEFT: FULL-SIZE VIDEO PLACEHOLDER (opens modal) */}
              <Col md="6">
                <div
                  role="button"
                  onClick={this.openModal}
                  onKeyDown={(e) => { if (e.key === "Enter") this.openModal(); }}
                  tabIndex={0}
                  style={{
                    width: "100%",
                    height: "420px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "#000",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    backgroundImage: videoBgSrc ? `url(${videoBgSrc})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <ModalVideo
                    channel="youtube"
                    isOpen={isOpen}
                    videoId={youtube_id || ""}
                    onClose={this.closeModal}
                  />

                  {/* Large translucent circle with play icon */}
                  <div
                    style={{
                      width: 110,
                      height: 110,
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "rgba(0,0,0,0.45)",
                      border: "3px solid rgba(255,255,255,0.85)",
                    }}
                    aria-hidden="true"
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M8 5v14l11-7L8 5z" fill="#fff" />
                    </svg>
                  </div>
                </div>
              </Col>

              {/* RIGHT: About content + counters */}
              <Col md="6">
                <div className="about-content">
                  <h4 className="about-title">About US</h4>

                  <p
                    className="about-para"
                    dangerouslySetInnerHTML={{
                      __html: descriptionHtml,
                    }}
                  />

                  <Row>
                    {displayCounters.slice(0, 3).map((item, idx) => {
                      const numeric = this.getNumericFromItem(item) || this.getNumericFromItem({ count: item.count || item.value || item.description });
                      return (
                        <Col sm="4" key={idx}>
                          <div className={`counter-box box${idx + 1} text-center`}>
                            <h3>
                              <CountUp end={numeric} duration={3} />
                              <i className="las la-plus" />
                            </h3>
                            <p>{item.title || `Counter ${idx + 1}`}</p>
                           
                          </div>
                        </Col>
                      );
                    })}
                  </Row>

                  <Link className="readmore-btn" to={process.env.PUBLIC_URL + "/about"}>
                    Read More
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </Styles>
    );
  }
}

export default AboutUs;
